import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../utils/fatch';
import styles from './passwordReset.module.css';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Step two of a password reset. The token comes from the emailed link. It is
 * checked here so a dead link is obvious before typing, and checked again on
 * the server when the new password is submitted.
 */
export function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [checking, setChecking] = useState(true);
    const [tokenError, setTokenError] = useState('');
    const [accountEmail, setAccountEmail] = useState('');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            try {
                const response = await fetch(`${API_URL}/auth/reset-password/${token}`);
                const data = await response.json();

                if (!response.ok || !data.valid) {
                    setTokenError(data.error || 'This reset link is not valid.');
                    return;
                }

                setAccountEmail(data.email);
            } catch (err) {
                console.error('Reset token check failed:', err);
                setTokenError('Could not reach the server.');
            } finally {
                setChecking(false);
            }
        };

        checkToken();
    }, [token]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (saving) return;

        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
            return;
        }
        if (password !== confirm) {
            setError('The two passwords do not match');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Could not change your password');
                return;
            }

            setDone(true);
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            console.error('Reset password error:', err);
            setError('Could not reach the server. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (checking) {
        return <div className={styles.page}><div className={styles.card}><p className={styles.sub}>Checking your link…</p></div></div>;
    }

    if (tokenError) {
        return (
            <div className={styles.page}>
                <div className={styles.card}>
                    <div className={styles.brand}>RoamRite</div>
                    <div className={styles.icon}>⏳</div>
                    <h1>This link doesn&apos;t work</h1>
                    <p className={styles.sub}>{tokenError}</p>
                    <Link to="/forgot-password" className={styles.primaryBtnLink}>Send me a new link</Link>
                    <Link to="/login" className={styles.ghostBtn}>Back to sign in</Link>
                </div>
            </div>
        );
    }

    if (done) {
        return (
            <div className={styles.page}>
                <div className={styles.card}>
                    <div className={styles.brand}>RoamRite</div>
                    <div className={styles.icon}>✅</div>
                    <h1>Password changed</h1>
                    <p className={styles.sub}>You can sign in with your new password now. Taking you there…</p>
                    <Link to="/login" className={styles.primaryBtnLink}>Sign in</Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.card}>
                <div className={styles.brand}>RoamRite</div>
                <div className={styles.icon}>🔒</div>
                <h1>Choose a new password</h1>
                <p className={styles.sub}>
                    For <strong>{accountEmail}</strong>
                </p>

                <form onSubmit={handleSubmit}>
                    <label className={styles.label} htmlFor="new-password">New password</label>
                    <div className={styles.passwordWrap}>
                        <input
                            id="new-password"
                            type={showPassword ? 'text' : 'password'}
                            className={styles.input}
                            placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={saving}
                            required
                            autoComplete="new-password"
                        />
                        <button
                            type="button"
                            className={styles.showBtn}
                            onClick={() => setShowPassword(prev => !prev)}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </button>
                    </div>

                    <label className={styles.label} htmlFor="confirm-password">Confirm password</label>
                    <input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        className={styles.input}
                        placeholder="Type it again"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        disabled={saving}
                        required
                        autoComplete="new-password"
                    />

                    {error && <p className={styles.error}>{error}</p>}

                    <button type="submit" className={styles.primaryBtn} disabled={saving || !password || !confirm}>
                        {saving ? 'Saving…' : 'Change my password'}
                    </button>
                </form>
            </div>
        </div>
    );
}
